import { useQuery } from '@apollo/client'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { GET_TAX_REPORT } from '../../graphql/reports-queries'
import { formatEuros } from '../../lib/format'
import {
  SectionTitle,
  StatCard,
  EmptyData,
  TableLoading,
  QueryError,
  formatMethod,
} from './shared'

// ─── Types ────────────────────────────────────────────────────────────────────

interface TaxReport {
  start_date: string
  end_date: string
  total_net_cents: number
  total_tax_cents: number
  total_gross_cents: number
  /**
   * Statiegeld — a waarborgsom, OUTSIDE the VAT base. It is in none of the
   * three totals above and in no by_tax_rate bucket, by design: it must not
   * appear on the VAT return. It reconciles the till instead:
   * total_gross_cents + total_deposit_cents == what was actually charged.
   */
  total_deposit_cents: number
  by_tax_rate: {
    tax_rate_percentage: number
    net_amount_cents: number
    tax_amount_cents: number
    gross_amount_cents: number
    order_line_count: number
  }[]
  by_payment_method: {
    payment_method: string
    net_amount_cents: number
    tax_amount_cents: number
    gross_amount_cents: number
    order_line_count: number
  }[]
  daily_breakdown: {
    date: string
    net_amount_cents: number
    tax_amount_cents: number
    gross_amount_cents: number
    /** Statiegeld that day — outside the VAT base, not in the three above. */
    deposit_total_cents: number
  }[]
}

// ─── Tax Report Tab ───────────────────────────────────────────────────────────

const TaxReportTab = ({ startDate, endDate }: { startDate: string; endDate: string }) => {
  const { data, loading, error, refetch } = useQuery<{ taxReport: TaxReport }>(GET_TAX_REPORT, {
    variables: { start: startDate, end: endDate },
    fetchPolicy: 'cache-and-network',
    skip: !startDate || !endDate,
  })

  const report = data?.taxReport

  if (loading && !report) return <TableLoading />
  if (error) return <QueryError message={`Failed to load tax report: ${error.message}`} />
  if (!report) return null

  const hasData = report.by_tax_rate.length > 0 || report.daily_breakdown.length > 0

  return (
    <div className="space-y-6">
      {/* Summary. Statiegeld gets its own card ONLY when there is some, and
          deliberately sits outside the three VAT figures: it is a waarborgsom,
          not turnover, and must never reach the VAT return. The fourth card is
          what makes the day reconcile against the payments report. */}
      <div className={`grid grid-cols-1 gap-4 ${report.total_deposit_cents > 0 ? 'sm:grid-cols-4' : 'sm:grid-cols-3'}`}>
        <StatCard label="Total Net (excl. VAT)" value={formatEuros(report.total_net_cents)} />
        <StatCard label="Total VAT" value={formatEuros(report.total_tax_cents)} />
        <StatCard label="Total Gross (incl. VAT)" value={formatEuros(report.total_gross_cents)} />
        {report.total_deposit_cents > 0 && (
          <StatCard label="Statiegeld (no VAT)" value={formatEuros(report.total_deposit_cents)} />
        )}
      </div>

      {report.total_deposit_cents > 0 && (
        <p className="text-sm text-gray-500">
          Statiegeld is a deposit, not turnover — it carries no VAT and is excluded from the
          figures above. Charged in this period:{' '}
          <span className="font-semibold text-gray-700">{formatEuros(report.total_deposit_cents)}</span>.
          Total charged to customers:{' '}
          <span className="font-semibold text-gray-700">
            {formatEuros(report.total_gross_cents + report.total_deposit_cents)}
          </span>.
        </p>
      )}

      {!hasData && <EmptyData />}

      {/* By VAT rate */}
      {report.by_tax_rate.length > 0 && (
        <div>
          <SectionTitle>By VAT Rate</SectionTitle>
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">VAT Rate</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Lines</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Net</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">VAT</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Gross</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {report.by_tax_rate.map((row) => (
                    <tr key={row.tax_rate_percentage} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3 font-medium text-gray-900">{row.tax_rate_percentage}%</td>
                      <td className="px-5 py-3 text-right text-gray-500">{row.order_line_count}</td>
                      <td className="px-5 py-3 text-right text-gray-700">{formatEuros(row.net_amount_cents)}</td>
                      <td className="px-5 py-3 text-right text-gray-700">{formatEuros(row.tax_amount_cents)}</td>
                      <td className="px-5 py-3 text-right font-semibold text-gray-900">{formatEuros(row.gross_amount_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* By payment method (tax view) */}
      {report.by_payment_method.length > 0 && (
        <div>
          <SectionTitle>By Payment Source</SectionTitle>
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Method</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Lines</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Net</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">VAT</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Gross</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {report.by_payment_method.map((row) => (
                    <tr key={row.payment_method} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3 font-medium text-gray-900">{formatMethod(row.payment_method)}</td>
                      <td className="px-5 py-3 text-right text-gray-500">{row.order_line_count}</td>
                      <td className="px-5 py-3 text-right text-gray-700">{formatEuros(row.net_amount_cents)}</td>
                      <td className="px-5 py-3 text-right text-gray-700">{formatEuros(row.tax_amount_cents)}</td>
                      <td className="px-5 py-3 text-right font-semibold text-gray-900">{formatEuros(row.gross_amount_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Daily breakdown */}
      {report.daily_breakdown.length > 0 && (
        <div>
          <SectionTitle>Daily Breakdown</SectionTitle>
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Net</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">VAT</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Gross</th>
                    {report.total_deposit_cents > 0 && (
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Statiegeld</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {report.daily_breakdown.map((row) => (
                    <tr key={row.date} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3 font-medium text-gray-900">{row.date}</td>
                      <td className="px-5 py-3 text-right text-gray-700">{formatEuros(row.net_amount_cents)}</td>
                      <td className="px-5 py-3 text-right text-gray-700">{formatEuros(row.tax_amount_cents)}</td>
                      <td className="px-5 py-3 text-right font-semibold text-gray-900">{formatEuros(row.gross_amount_cents)}</td>
                      {report.total_deposit_cents > 0 && (
                        <td className="px-5 py-3 text-right text-gray-500">
                          {row.deposit_total_cents > 0 ? formatEuros(row.deposit_total_cents) : '—'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
    </div>
  )
}

export default TaxReportTab
